import { Box, Grid2 as Grid, Typography } from "@mui/material";
import {
  InvoicePaymentCallbackMessage,
  PaymentStatus,
  TransactionType,
  InvoicePaymentStatus,
} from "../../../enums";
import { InvoicePaymentStatusChip } from "../../common/chips";
import { PdfViewer } from "../../common/pdf-viewer";
import {
  InvoiceOverviewInfo,
  PaymentRecordsFilters,
  PaymentsInfo,
} from "../../../types";
import {
  formatCurrency,
  formatDate,
  getDateRange,
  getInvoiceStatus,
  theme,
} from "../../../utils";
import { useEffect, useState } from "react";
import {
  isReceivePaymentFormActiveAtom,
  paymentsDataGridPaginationAtom,
  userAtom,
} from "../../../atoms";
import { useAtom } from "jotai";
import { PaymentDialog } from "../../payment/PaymentDialog";
import { DataGrid } from "../../common";
import { GridColDef, GridSortDirection } from "@mui/x-data-grid";
import { useFetchPaymentRecordsQuery } from "../../../api";
import moment from "moment";

type InvoiceOverviewProps = {
  invoiceOverview: InvoiceOverviewInfo;
  pdfFileLink?: string;
  refetch: () => void;
};

export const InvoiceOverview = ({
  invoiceOverview,
  pdfFileLink,
  refetch,
}: InvoiceOverviewProps) => {
  const [user] = useAtom(userAtom);
  const [paymentsPaginationModel, setPaymentsPaginationModel] = useAtom(
    paymentsDataGridPaginationAtom,
  );
  const [isReceivePaymentFormActive, setIsReceivePaymentFormActive] = useAtom(
    isReceivePaymentFormActiveAtom,
  );
  const [paymentRecords, setPaymentRecords] = useState<PaymentsInfo>();
  const [paymentRecordsFilters, setPaymentRecordsFilters] =
    useState<PaymentRecordsFilters>({
      sortBy: "transactionDate",
      sortOrder: "desc",
      paymentStatus: PaymentStatus.All,
      transactionType: TransactionType.All,
    });

  const fetchPaymentRecordsQuery = useFetchPaymentRecordsQuery(
    {
      orgId: invoiceOverview.orgId,
      accountId: invoiceOverview.accountId,
      invoiceNumber: invoiceOverview.invoiceNumber,
      paymentStatus: PaymentStatus.All,
      transactionType: TransactionType.All,
      filters: {
        page: paymentRecordsFilters?.page ? paymentRecordsFilters?.page + 1 : 1,
        pageSize: paymentRecordsFilters?.pageSize,
        sortBy: paymentRecordsFilters?.sortBy,
        sortOrder: paymentRecordsFilters?.sortOrder,
        fromDate: moment
          .utc(invoiceOverview.invoiceDate)
          .startOf("day")
          .toISOString(),
        toDate: getDateRange("today").endDate,
      },
    },
    { enabled: invoiceOverview.orgId ? true : false },
  );

  const handleClosePaymentDialog = (message: InvoicePaymentCallbackMessage) => {
    setIsReceivePaymentFormActive(false);
    if (message === InvoicePaymentCallbackMessage.PaymentSuccess) {
      refetch();
    }
  };

  const columns: GridColDef[] = [
    {
      field: "transactionDate",
      headerName: "Date",
      width: 180,
      editable: false,
      valueFormatter: (value) => {
        return formatDate(value, "dddd - MMMM DD,YYYY");
      },
    },
    {
      field: "paymentMethod",
      headerName: "Payment Method",
      width: 160,
      headerAlign: "center",
      align: "center",
      editable: false,
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 100,
      headerAlign: "center",
      align: "center",
      editable: false,
      valueFormatter: (value, row) => {
        const isRefund = row.transactionType === TransactionType.Refund;
        return `${isRefund ? "-" : ""}$ ${parseFloat(value).toFixed(2)}`;
      },
    },
  ];

  useEffect(() => {
    handleClosePaymentDialog(InvoicePaymentCallbackMessage.None);
  }, []);

  useEffect(() => {
    if (fetchPaymentRecordsQuery.isSuccess) {
      setPaymentRecords(fetchPaymentRecordsQuery.data);
    }
  }, [fetchPaymentRecordsQuery.isSuccess, fetchPaymentRecordsQuery.data]);

  useEffect(() => {
    setPaymentRecordsFilters({
      ...paymentRecordsFilters,
      page: paymentsPaginationModel.page,
      pageSize: paymentsPaginationModel.pageSize,
    });
  }, [paymentsPaginationModel]);

  return (
    <Grid
      container
      size={12}
      direction={{ xs: "column", md: "row" }}
      sx={{
        display: "flex",
        justifyContent: "space-between",
      }}
      spacing={3}
    >
      <Grid
        container
        size={{ xs: 12, md: 12, lg: 6 }}
        sx={{ px: 3 }}
        gap={3}
        direction="column"
      >
        <Grid
          container
          size={12}
          sx={{ height: "fit-content", display: "flex", gap: 1 }}
        >
          {user && (
            <InvoicePaymentStatusChip
              status={getInvoiceStatus(
                invoiceOverview.invoiceStatus,
                invoiceOverview.dueDate,
                user.roleCode,
                invoiceOverview.isTransactionInitiated,
              )}
            />
          )}
        </Grid>
        <Grid container size={12} spacing={3}>
          <Grid size={{ lg: 4, md: 4, xs: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  color: "#606060",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                Invoice Amount
              </Typography>
              <Typography
                sx={{
                  color: "#000",
                  textAlign: "left",
                }}
              >
                {formatCurrency(invoiceOverview.invoiceAmount)}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ lg: 4, md: 4, xs: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  color: "#606060",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                Amount Paid
              </Typography>
              <Typography
                sx={{
                  color: "#000",
                  textAlign: "left",
                }}
              >
                {formatCurrency(invoiceOverview.paidAmount)}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ lg: 4, md: 4, xs: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  color: "#606060",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                Outstanding Balance
              </Typography>
              <Typography
                sx={{
                  color: "#000",
                  textAlign: "left",
                }}
              >
                {formatCurrency(invoiceOverview.balanceAmount)}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ lg: 4, md: 4, xs: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  color: "#606060",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                Wash Charges
              </Typography>
              <Typography
                sx={{
                  color: "#000",
                  textAlign: "left",
                }}
              >
                {formatCurrency(invoiceOverview.totalWashCharge)}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ lg: 4, md: 4, xs: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  color: "#606060",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                Misc Charges
              </Typography>
              <Typography
                sx={{
                  color: "#000",
                  textAlign: "left",
                }}
              >
                {formatCurrency(invoiceOverview.totalMiscCharge)}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ lg: 4, md: 4, xs: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  color: "#606060",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                Total Fees
              </Typography>
              <Typography
                sx={{
                  color: "#000",
                  textAlign: "left",
                }}
              >
                {formatCurrency(invoiceOverview.totalFees)}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ lg: 4, md: 4, xs: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  color: "#606060",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                Total Discounts
              </Typography>
              <Typography
                sx={{
                  color: "#000",
                  textAlign: "left",
                }}
              >
                {formatCurrency(invoiceOverview.totalDiscounts)}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ lg: 4, md: 4, xs: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  color: "#606060",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                Due Date
              </Typography>
              <Typography
                sx={{
                  color: "#000",
                  textAlign: "left",
                }}
              >
                {formatDate(invoiceOverview.dueDate)}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ lg: 12, md: 12, xs: 12 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  color: "#606060",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                Paid On
              </Typography>
            </Box>
            <Grid>
              <DataGrid
                columns={columns}
                rows={paymentRecords?.items || []}
                loading={
                  fetchPaymentRecordsQuery.isLoading ||
                  fetchPaymentRecordsQuery.isRefetching
                }
                sortModel={[
                  {
                    field: paymentRecordsFilters.sortBy || "",
                    sort:
                      (paymentRecordsFilters.sortOrder as GridSortDirection) ||
                      null,
                  },
                ]}
                checkboxSelection={false}
                paginationModel={paymentsPaginationModel}
                setPaginationModel={setPaymentsPaginationModel}
                totalRecords={paymentRecords?.totalItems}
                paginationMode="server"
                rowCount={
                  paymentRecords?.totalItems ? paymentRecords?.totalItems : 0
                }
                pageSize={paymentRecords?.pageSize}
                sx={{
                  backgroundColor: theme.palette.secondary.light,
                  padding: "20px",
                  paddingTop: "0px",
                  "& .MuiSvgIcon-root": {
                    fontSize: 16,
                  },
                  borderBottomLeftRadius: "10px",
                  borderBottomRightRadius: "10px",
                  height: "70vh",
                  "& .css-1rd68wh-MuiGrid2-root": {
                    display: "none",
                  },
                  "& css-g15tsz": {
                    display: "none",
                  },
                  "& .css-49dqqg-MuiGrid2-root": {
                    width: "100%",
                  },
                  "& .css-wybscc": {
                    width: "100%",
                  },
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        container
        size={{ xs: 12, md: 12, lg: 6 }}
        sx={{
          boxShadow: "0px 0px 14px 0px #00000040",
          overflow: "hidden",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {pdfFileLink && <PdfViewer url={pdfFileLink} />}
      </Grid>
      <PaymentDialog
        invoiceOverview={invoiceOverview}
        open={isReceivePaymentFormActive}
        handleClose={handleClosePaymentDialog}
      />
    </Grid>
  );
};
